/**
 * PDF build, merge, and download logic.
 * Extracted from FarmDetailsModal.tsx.
 */

import { PDFDocument } from "pdf-lib";
import { pdf } from "@react-pdf/renderer";

/**
 * Build PDF blob from React PDF component.
 */
export async function buildPdfBlob(
  jsxElement: React.ReactElement,
): Promise<Blob> {
  return await pdf(jsxElement).toBlob();
}

/**
 * Merge attachment PDFs into main PDF.
 */
export async function mergeAttachedPdfs(
  mainPdfBuffer: ArrayBuffer,
  attachedPdfs: { buffer: ArrayBuffer }[],
): Promise<Uint8Array> {
  const mergedPdf = await PDFDocument.load(mainPdfBuffer);

  for (const { buffer } of attachedPdfs) {
    const attachmentPdf = await PDFDocument.load(buffer);
    const copiedPages = await mergedPdf.copyPages(
      attachmentPdf,
      attachmentPdf.getPageIndices(),
    );
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  return await mergedPdf.save();
}

/**
 * Download PDF blob with given filename.
 */
export function downloadPdfBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Build and download merged PDF.
 */
export async function buildAndDownloadMergedPdf(
  mainPdfElement: React.ReactElement,
  attachedPdfs: { buffer: ArrayBuffer }[],
  filename: string,
): Promise<void> {
  const mainPdfBlob = await buildPdfBlob(mainPdfElement);
  const mainPdfBuffer = await mainPdfBlob.arrayBuffer();
  const mergedPdfBytes = await mergeAttachedPdfs(mainPdfBuffer, attachedPdfs);
  const finalBlob = new Blob([new Uint8Array(mergedPdfBytes)], {
    type: "application/pdf",
  });

  downloadPdfBlob(finalBlob, filename);
}
